/**
 * Contenu éditorial de la vitrine (#808).
 *
 * **Rien ici ne doit être inventé.** Chaque module cité correspond à un écran qui
 * existe (`src/app/admin/(protected)/*`, `src/app/(protected)/*`), chaque geste
 * mobile décrit à un écran réel de l'application, capturé et non reconstitué.
 * Pas de logo client, pas de témoignage, pas de chiffre de croissance : la
 * précédente version en publiait, tous fictifs.
 *
 * **La console éditeur n'apparaît pas.** Elle n'est pas commercialisée — un
 * prospect ne doit pas la découvrir ici, encore moins dans une formule.
 */

import {
  BadgeEuro,
  Banknote,
  Building2,
  CalendarCheck,
  FileSignature,
  type LucideIcon,
  Monitor,
  Receipt,
  ScrollText,
  ShieldCheck,
  Smartphone,
  UserRound,
} from 'lucide-react';

/*
  **Aucun lien vers l'espace connecté** (#1375).

  Le pied de page en portait un, discret, depuis #814 — la barre de navigation en avait été
  débarrassée pour ne pas concurrencer l'appel à l'action commercial. Il n'y en a plus du tout : on
  ne se connecte pas depuis la vitrine. Chaque organisation est servie sur son sous-domaine,
  `<slug>.suivora-app.com`, et c'est là que son espace vit — depuis l'apex, le lien ouvrait un
  formulaire qui n'est celui d'aucune organisation.
*/

/**
 * Appel à l'action unique de la vitrine, répété à l'identique dans la barre de
 * navigation, le hero et le bloc final. Une seule action, une seule destination —
 * deux boutons d'intentions voisines côte à côte se diluent l'un l'autre.
 *
 * **« Démo » et non « essai gratuit » (#1342)** : le libellé promettait un mois
 * d'accès en libre-service, que le parcours ne délivre pas. Ce qui est proposé
 * est un environnement préparé et présenté — et l'adresse le dit aussi, sans
 * quoi l'URL contredirait chaque bouton de la page.
 */
export const TRIAL_HREF = '/demander-une-demo';
export const TRIAL_LABEL = 'Demander une démo';

/* ------------------------------------------------------------------ Nav ---- */

/**
 * Les entrées du bandeau — **des pages, plus des ancres** (#1322).
 *
 * Une ancre ne mène quelque part que depuis l'accueil : depuis les mentions légales ou la demande
 * d'essai, le clic ne faisait rien. Chaque section a désormais son adresse, qu'on peut envoyer par
 * mail, mettre en signet et référencer.
 *
 * `anchor` reste l'ancre correspondante sur l'accueil, où toutes les sections se suivent.
 */
export const NAV_LINKS: { href: string; anchor: string; label: string }[] = [
  /*
    L'accueil en premier (#1342) : depuis une page de section, le seul retour était le wordmark,
    qu'on ne lit pas comme un lien. Et c'est l'accueil qui porte le métier et les modules.
  */
  { href: '/', anchor: '#top', label: 'Accueil' },
  /*
    **Fonctionnalités et Avantages au bandeau** (#2215) : ce sont les deux questions qu'un
    prospect se pose avant tout le reste — « couvrez-vous mon métier ? » et « pourquoi vous
    plutôt qu'un autre ? ». Les laisser au fond de l'accueil obligeait à faire défiler pour
    trouver ce pour quoi on est venu.
  */
  { href: '/fonctionnalites', anchor: '#fonctionnalites', label: 'Fonctionnalités' },
  { href: '/avantages', anchor: '#avantages', label: 'Avantages' },
  { href: '/applications-mobiles', anchor: '#mobile', label: 'Applications mobiles' },
  { href: '/espace-web', anchor: '#espace-web', label: 'Espace web' },
  { href: '/tarifs', anchor: '#tarifs', label: 'Tarifs' },
];

/**
 * Ce que l'accueil porte **en entier** (#1328) : le métier et les modules.
 *
 * Ils ne sont plus au bandeau — leur contenu est sous les yeux du visiteur dès l'accueil, et une
 * entrée de menu qui renvoie vers ce qu'on est en train de lire n'apprend rien. Leurs pages restent
 * en place et gardent leur adresse, pour un lien envoyé ou un signet.
 */
export const HOME_SECTIONS = [
  { href: '/le-metier', anchor: '#metier', label: 'Le métier' },
  { href: '/modules', anchor: '#modules', label: 'Modules' },
];

/**
 * Les trois sections **résumées** à l'accueil, avec un lien vers leur page (#1324, #1328).
 *
 * Le métier et les modules n'y sont pas : ils se lisent en entier à l'accueil, et un résumé de ce
 * qui est déjà sous les yeux n'apprend rien. Les trois autres se prêtent au résumé — on y va pour
 * un détail précis : un écran, un appareil, un montant.
 *
 * L'ordre est celui du bandeau, et les adresses en sont tirées : deux listes finiraient par ne
 * plus concorder, et c'est le lien de la vitrine qui casserait.
 */
export const SECTION_TEASERS: {
  href: string;
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  text: string;
}[] = [
  {
    href: '/applications-mobiles',
    icon: Smartphone,
    eyebrow: 'Applications mobiles',
    title: 'Vos consultants, sans passer par un ordinateur',
    text: 'CRA, notes de frais et documents depuis iOS et Android — et les validations pour ceux qui les attendent.',
  },
  {
    href: '/espace-web',
    icon: Monitor,
    eyebrow: 'Espace web',
    /*
      **Deux**, jamais trois (#1363). La console éditeur avait reparu ici alors que la règle de ce
      fichier l'exclut depuis le début : elle n'est pas commercialisée, et un prospect qui apprend
      son existence se demande ce qu'on y voit de ses données.
    */
    title: 'Deux espaces, un seul produit',
    text: 'Le consultant et l’administration du cabinet : chacun ne voit que le sien, sur les mêmes données.',
  },
  {
    href: '/tarifs',
    icon: Receipt,
    eyebrow: 'Tarifs',
    title: 'Vous payez au porté facturable',
    text: 'La grille en vigueur, telle qu’elle est facturée — dégressive à mesure que vous grandissez.',
  },
];

/* ----------------------------------------------------------------- Hero ---- */

/** Les trois arguments de la ligne de confiance, sous les boutons du hero. */
export const HERO_PROOFS: { icon: LucideIcon; label: string }[] = [
  { icon: ShieldCheck, label: 'Hébergement en France' },
  { icon: Building2, label: 'Cloisonné par organisation' },
  { icon: Smartphone, label: 'Deux applications iOS & Android' },
];

/* -------------------------------------------------------------- Le métier ---- */

/**
 * Ce que couvre l'ERP, exprimé en problèmes de gestionnaire de portage plutôt
 * qu'en fonctionnalités.
 */
export const TRADE_POINTS: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: FileSignature,
    title: 'Le porté, de son contrat à sa sortie',
    text: 'Régime CDI en portage ou association, pièces d’identité, coordonnées bancaires, avenants : le dossier se constitue par un parcours guidé et se signe électroniquement, sans échange de PDF par mail.',
  },
  {
    icon: CalendarCheck,
    title: 'Le mois déclaré, puis validé',
    text: 'Compte-rendu d’activité, absences, notes de frais et titres-restaurant arrivent au même endroit. Vos RH disposent d’un poste de validation dédié, pas d’une boîte de réception.',
  },
  {
    icon: BadgeEuro,
    title: 'La facture client et la rémunération',
    text: 'La facturation se déclenche depuis la mission et l’activité validée. Les distributions de dividendes des associés suivent le même circuit, avec la même trace.',
  },
];

/* ------------------------------------------------------------- Mobile ---- */

/**
 * Arguments de l'application mobile. Chacun est lisible sur le triptyque ou
 * dans le produit : les gestes du CRA, le justificatif photo, le barème
 * kilométrique (`features/expenses/mileage.ts`), la signature Yousign
 * (`SendToSignModal`), les notifications par appareil iOS/Android.
 */
export const CONSULTANT_APP_FEATURES: { title: string; text: string }[] = [
  {
    title: 'Le CRA se remplit au doigt',
    text: 'Un tap pose une demi-journée, deux une journée pleine, un appui long bascule en télétravail. Un mois se déclare en moins d’une minute, sans clavier.',
  },
  /*
    **Ce que la lecture fait, et rien de plus** (#2344, suivora-api#1778).

    ✅ Le montant, la date et l'enseigne sont repris — ce sont les trois champs que le serveur écrit.

    ❌ Pas « tout est rempli » : la **catégorie n'est pas lue**. Une enseigne se lit sur un ticket,
       pas un vocabulaire de six valeurs ; elle est devinée, « Autre » par défaut.

    ❌ Aucun taux de fiabilité. L'exactitude par champ **n'a pas été mesurée** — le serveur le dit,
       et c'est pourquoi il sert la confiance sans seuil. Promettre un pourcentage ici serait
       l'inventer.

    ⚠️ L'argument le plus solide n'est pas la reconnaissance, c'est que **le justificatif ne sort
    pas de l'infrastructure** : la lecture se fait sur une référence, l'image n'est ni téléchargée
    ni copiée. C'est ce qui a écarté les modèles de vision, pourtant meilleurs — et devant des
    cabinets qui manipulent des pièces comptables, cela pèse plus qu'un chiffre.
  */
  {
    title: 'Le ticket se lit tout seul',
    text: 'Photographiez le justificatif : le montant, la date et l’enseigne en sont repris, et vous n’avez plus qu’à vérifier. Ce que vous avez déjà saisi n’est jamais écrasé.',
  },
  {
    title: 'Le justificatif ne quitte pas nos serveurs',
    text: 'La lecture se fait là où l’image est déposée : elle n’est ni téléchargée, ni copiée, ni envoyée ailleurs. Elle ne sort ni de nos serveurs, ni de l’Europe.',
  },
  {
    title: 'Une note de frais, une photo',
    text: 'Le justificatif est capturé à la volée. Les frais kilométriques appliquent le barème en vigueur, et l’app signale le justificatif manquant avant l’envoi.',
  },
  {
    title: 'Les documents se signent sur le téléphone',
    text: 'Contrats, avenants et attestations partent en signature électronique et se signent depuis l’application, sans imprimante ni scanner.',
  },
  {
    title: 'Sa situation, sans avoir à la demander',
    text: 'Chiffre d’affaires du mois, CA cumulé sur la mission, ancienneté, frais remboursés : le porté a ses chiffres en page d’accueil.',
  },
];

/**
 * Les deux gestes de l'application consultant, chacun avec son écran (#1195).
 *
 * Ils remplacent le téléphone qui était **dessiné en JSX** ici. Le commentaire du composant
 * justifiait le dessin ainsi : « ce qui manque à une capture, c'est le geste — un tap, deux taps,
 * un appui long ». C'était vrai jusqu'à ces captures : l'application affiche elle-même cette aide,
 * mot pour mot, en tête de son calendrier. La reconstitution n'apportait donc plus rien qu'un
 * risque de diverger du produit.
 */
export const CONSULTANT_APP_MOMENTS: {
  src: string;
  alt: string;
  title: string;
  text: string;
}[] = [
  {
    src: '/images/consultant-mobile-duo-cra-frais.png',
    alt: 'Le compte-rendu d’activité et les notes de frais dans l’application consultant',
    title: 'Son mois se déclare au doigt',
    text: 'Un tap pose une demi-journée, deux une journée pleine, un appui long bascule en télétravail — l’application le rappelle en tête de calendrier. Les frais suivent, justificatif photographié à la volée, et ce qui manque est signalé avant l’envoi.',
  },
  {
    src: '/images/consultant-mobile-duo-accueil-finances.png',
    alt: 'L’accueil du consultant et le détail de ses finances mois par mois',
    title: 'Ses chiffres, sans avoir à les demander',
    text: 'Chiffre d’affaires du mois et de la mission, avancement du compte-rendu, frais engagés : tout est en page d’accueil. Le détail va jusqu’au mois — CA facturé, salaire, notes de frais — et le relevé se télécharge.',
  },
];

/** Les relances qui ne sont plus à faire à la main, côté consultant. */
export const MOBILE_PUSH_EXAMPLES = [
  'CRA à envoyer',
  'Justificatif manquant',
  'Document à signer',
  'Frais remboursés',
];

/**
 * L'application d'administration — **la seconde application**, et l'argument que
 * la version précédente de la page ne faisait même pas soupçonner.
 *
 * Un gestionnaire de portage n'est pas à son bureau : ce qui coûte cher, ce
 * n'est pas la validation d'un CRA, c'est de l'apprendre trois jours trop tard.
 */
export const ADMIN_APP_FEATURES: { title: string; text: string }[] = [
  {
    title: 'Valider un CRA rentré en retard',
    text: 'Un consultant envoie son compte-rendu le 3 au soir, la veille de la facturation. La validation se fait dans l’application, sans rouvrir le back-office.',
  },
  {
    title: 'Onboarder un consultant',
    text: 'Créer le compte, lancer le parcours d’arrivée et suivre les pièces qui manquent encore — un nouveau porté n’attend pas votre retour de déplacement.',
  },
  {
    title: 'Créer une mission',
    text: 'Client, période, affectation du consultant : une mission se monte depuis le téléphone, entre deux rendez-vous, et non le soir venu.',
  },
  {
    title: 'Ne rien laisser passer',
    text: 'Tout ce qui attend une décision remonte en notification : CRA à valider, note de frais, document à signer, demande d’absence.',
  },
];

/**
 * Les trois gestes de l'application d'administration, chacun avec son écran (#1195).
 *
 * De **vraies** captures, contrairement au téléphone du bloc consultant, dont l'écran est dessiné
 * en JSX faute d'en avoir une : un produit qu'on vend en montrant une reconstitution vaut moins
 * qu'un produit qu'on montre.
 *
 * Chaque visuel est un **duo** — un contexte et l'action qu'on y mène —, et chacun répond à l'un
 * des arguments ci-dessus. C'est ce qui fait passer le bloc d'une liste de promesses à une
 * démonstration : la promesse est adossée à l'écran qui la tient.
 *
 * Les fichiers portent déjà leur châssis d'iPhone, encoche et boutons compris : rien à encadrer
 * autour. Tous au même format (1400 × 1359), ce qui les aligne sans décalage d'une rangée à
 * l'autre — c'est cette régularité qui rend l'alternance lisible.
 */
export const ADMIN_APP_MOMENTS: { src: string; alt: string; title: string; text: string }[] = [
  {
    src: '/images/admin-mobile-duo-validation.png',
    alt: 'L’accueil de l’application d’administration et la validation d’un compte-rendu d’activité',
    title: 'Voir ce qui attend, et le traiter',
    text: 'L’accueil dit combien de dossiers réclament une décision, et par lequel commencer. Un compte-rendu rentré la veille de la facturation se valide dans la foulée, sans rouvrir le back-office.',
  },
  {
    src: '/images/admin-mobile-duo-mission.png',
    alt: 'La liste des missions et la création d’une mission en trois étapes',
    title: 'Monter une mission entre deux rendez-vous',
    text: 'Client, période, affectation : l’essentiel tient en une étape, les deux suivantes affinent le contrat et peuvent attendre. La mission existe avant le retour au bureau.',
  },
  {
    src: '/images/admin-mobile-duo-consultant.png',
    alt: 'La création d’un consultant et l’annuaire des consultants',
    title: 'Faire entrer un consultant',
    text: 'On ne saisit que l’essentiel : le porté complète lui-même son profil par le lien d’onboarding. L’annuaire montre ensuite qui est en mission, qui reste à valider, et ce qui manque encore.',
  },
];

/* ---------------------------------------------------------- Vidéo démo ---- */

/**
 * Identifiant YouTube de la vidéo de démonstration, **à fournir**.
 *
 * `null` → la section entière ne se rend pas. C'est volontairement différent du
 * visuel web ci-dessous, qui réserve sa place : un cadre d'image vide se lit
 * comme « visuel à venir », un lecteur vidéo vide se lit comme « la page est
 * cassée ».
 *
 * Renseigner l'identifiant seul (`dQw4w9WgXcQ`), pas l'URL complète.
 */
export const DEMO_VIDEO_ID: string | null = null;

/** Titre annoncé sur la façade, et attribut `title` de l'iframe une fois chargée. */
export const DEMO_VIDEO_TITLE = 'Suivora en deux minutes';

/* ------------------------------------------------------------ Espace web ---- */

/**
 * Visuel de l'espace web (back-office), **à fournir**.
 *
 * `null` tant que le fichier n'existe pas : la section réserve alors sa place et
 * affiche un cadre neutre, plutôt qu'un `next/image` pointant vers un chemin
 * absent — qui échouerait au build, pas au moment où on s'en apercevrait.
 *
 * Pour l'installer : déposer le fichier dans `public/images/`, renseigner le
 * chemin ici et ses dimensions réelles dans `WEB_APP_MOCK_SIZE`.
 */
export const WEB_APP_MOCK: string | null = '/images/web-tableau-de-bord.png';

/**
 * Dimensions intrinsèques du visuel ci-dessus, à ajuster avec lui.
 *
 * Aligné sur les captures réellement produites (2256 × 1432). L'ancien `2400 × 1500` était une
 * estimation : `object-cover` aurait rogné le haut et le bas de l'image pour tenir le ratio
 * déclaré, sans que rien ne le signale.
 */
export const WEB_APP_MOCK_SIZE = { width: 2256, height: 1432 };

/** Ce que le grand écran apporte, et que le mobile ne remplace pas. */
/**
 * Ce que le grand écran apporte **au consultant** (#1195).
 *
 * La section ne parlait que du back-office, alors que la page vend deux espaces et que le bloc
 * mobile, lui, traite les deux applications. Un porté qui lisait la vitrine ne voyait jamais à quoi
 * ressemblait le sien sur ordinateur.
 *
 * Les deux écrans retenus sont ceux où la surface change vraiment quelque chose : le mois entier
 * d'un seul coup d'œil, et un graphe que le téléphone ne peut que résumer.
 */
export const CONSULTANT_WEB_MOMENTS: {
  src: string;
  alt: string;
  title: string;
  text: string;
}[] = [
  {
    src: '/images/consultant-web-cra.png',
    alt: 'La feuille de temps d’un consultant sur navigateur',
    title: 'Le mois entier sous les yeux',
    text: 'Le calendrier complet, les jours travaillés, les absences et les astreintes au même endroit — et « Remplir le mois » pour les cas où rien ne change. Le téléphone fait la retouche quotidienne ; l’ordinateur fait le mois.',
  },
  {
    src: '/images/consultant-web-finances.png',
    alt: 'Le suivi financier d’un consultant sur navigateur',
    title: 'Ce qu’il facture, ce qu’il touche',
    text: 'Chiffre d’affaires, salaire net, dividendes et notes de frais sur la même échelle, mois après mois, avec le taux de conversion. C’est la lecture qu’un écran de téléphone ne peut que résumer.',
  },
];

export const WEB_APP_POINTS: string[] = [
  'Les listes et les tableaux, paginés et filtrables',
  'La facturation et les exports comptables',
  'Les statistiques et les rapports',
  'Les rôles, les permissions et le journal d’audit',
];

/* ------------------------------------------------------------ Modules ---- */

export const MODULE_DOMAINS: {
  icon: LucideIcon;
  title: string;
  text: string;
  items: string[];
}[] = [
  {
    icon: UserRound,
    title: 'Consultants & contrats',
    text: 'Le dossier du porté, complet et à jour.',
    items: [
      'Annuaire et fiches consultants',
      'Onboarding guidé',
      'Régimes CDI portage et association',
      'Documents et signature électronique',
      'Absences et congés',
    ],
  },
  {
    icon: CalendarCheck,
    title: 'Activité & justificatifs',
    text: 'Ce que le porté déclare, ce que les RH valident.',
    items: [
      'Comptes-rendus d’activité mensuels',
      'Notes de frais et barème kilométrique',
      'Titres-restaurant',
      'Poste de validation RH',
    ],
  },
  {
    icon: Building2,
    title: 'Commerce & missions',
    text: 'D’où vient l’affaire, et qui elle rémunère.',
    items: [
      'Clients et signataires',
      'Missions et affectations',
      'Apporteurs d’affaires et commissions',
      'Cooptation',
    ],
  },
  {
    icon: Banknote,
    title: 'Finance & pilotage',
    text: 'Ce qui sort en facture, ce qui remonte en chiffre.',
    items: [
      'Facturation par mission',
      'Dividendes des associés',
      'Rapports et exports',
      'Tableaux de bord et statistiques',
    ],
  },
];

/** Le reste du périmètre, en une ligne — tous ces écrans existent. */
export const MODULE_EXTRAS = [
  'Rôles et permissions',
  'Journal d’audit',
  'Imports en masse',
  'Annonces internes',
  'Base de connaissance',
  'Support',
  'Intégrations',
  'Demandes de suppression',
];

/* ------------------------------------------------------------- Espaces ---- */

/** Deux espaces, et deux seulement : la console éditeur n'est pas un produit. */
export const SPACES: {
  icon: LucideIcon;
  tag: string;
  title: string;
  text: string;
  points: string[];
}[] = [
  {
    icon: UserRound,
    tag: 'Consultant',
    title: 'Il déclare, il justifie, il signe',
    text: 'Sur mobile au quotidien, sur le web quand il faut un grand écran. Le même compte, les mêmes données.',
    points: [
      'CRA, absences et notes de frais',
      'Documents et signature électronique',
      'Chiffre d’affaires et missions',
      'Cooptation et annonces internes',
    ],
  },
  {
    icon: Building2,
    tag: 'Administration & RH',
    title: 'Elles pilotent, valident, facturent',
    text: 'Le back-office complet de la société de portage, avec des droits accordés module par module.',
    points: [
      'Consultants, clients et missions',
      'Validation des CRA et des frais',
      'Facturation et dividendes',
      'Statistiques, rapports et audit',
    ],
  },
];

/* ---------------------------------------------------------------- Cycle ---- */

export const CYCLE_STEPS: { n: string; title: string; text: string }[] = [
  {
    n: '01',
    title: 'Le porté déclare',
    text: 'CRA du mois, absences et notes de frais, saisis depuis l’application mobile au fil des jours.',
  },
  {
    n: '02',
    title: 'Les RH valident',
    text: 'Un poste de validation dédié reprend les écarts de jours et les justificatifs manquants, sans ressaisie.',
  },
  {
    n: '03',
    title: 'La facture part',
    text: 'La facturation s’appuie sur la mission et l’activité validée. Commissions d’apporteurs et dividendes suivent.',
  },
];

/* ------------------------------------------------------------ Confiance ---- */

export const TRUST_POINTS: { icon: LucideIcon; title: string; text: string }[] = [
  {
    icon: Building2,
    title: 'Cloisonnement par organisation',
    // Aucune promesse d'identité visuelle par client : la personnalisation de
    // marque n'est pas commercialisée, elle sert Freemakers seul.
    text: 'Chaque organisation est servie sur son propre sous-domaine, avec ses données, ses comptes et ses droits — rien n’est partagé entre deux entités.',
  },
  {
    icon: ShieldCheck,
    title: 'Rôles et permissions explicites',
    text: 'Un accès est un droit accordé, pas un statut hérité. Les rôles se composent module par module.',
  },
  {
    icon: ScrollText,
    title: 'Journal d’audit',
    text: 'Les actions sensibles laissent une trace horodatée, consultable, qui survit à la personne qui les a faites.',
  },
  {
    icon: FileSignature,
    title: 'Vos obligations RGPD',
    text: 'Les demandes de suppression de compte sont instruites dans l’outil, et l’hébergement des données est en France.',
  },
];

/* --------------------------------------------------------------- Tarifs ---- */

/**
 * **Aucun montant ici, et c'est délibéré.** La grille en vigueur est lue chez
 * Stripe et servie par l'API (#666) : la section l'affiche telle qu'elle est
 * facturée, depuis la route publique (suivora-api#970). Recopier des prix dans
 * le front est exactement ce que `shared/libs/pricingGrid.ts` proscrit — deux
 * copies divergent, et celle qui facture n'est pas celle qui est affichée.
 *
 * Ces trois points décrivent le **modèle**, qui lui ne bouge pas : ils tiennent
 * à côté de la grille, et tiennent seuls le jour où elle n'est pas lisible.
 */
export const PRICING_POINTS: { title: string; text: string }[] = [
  {
    /*
      La règle, dite telle qu'elle facture (suivora-api `billing-usage.types.ts`) : au moins une
      facture **émise** sur les six mois calendaires qui se terminent avec le mois facturé.

      « Consultants portés actifs » était vague au point d'être faux : « actif » n'est le critère de
      rien, et personne ne peut vérifier son propre compteur avec ça. Le mot juste est « facturé »,
      et il se compte.
    */
    title: 'Un seul compteur : les portés que vous avez facturés',
    text: 'Un consultant compte s’il a au moins une facture émise sur les six derniers mois. Ni par module, ni par utilisateur du back-office, ni par document signé.',
  },
  {
    title: 'Dégressif par palier',
    text: 'Le tarif du palier atteint s’applique à tout le volume. Plus vous portez de consultants, moins chacun vous coûte.',
  },
  {
    title: 'Tout est compris',
    text: 'Chaque module, les deux espaces et les applications mobiles. Pas d’option facturée à part, pas d’engagement de durée.',
  },
];
